import axios from 'axios';

// Configuración base de la API
// Con el proxy configurado en package.json, usamos rutas relativas
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar el token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('odoo_session_id');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para manejar respuestas
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado o inválido
          localStorage.removeItem('odoo_session_id');
          localStorage.removeItem('user_info');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Autenticación
  async login(username, password, database = 'ia') {
    try {
      const response = await this.api.post('/web/session/authenticate', {
        jsonrpc: '2.0',
        method: 'call',
        params: {
          db: database,
          login: username,
          password: password,
        },
      });

      if (response.data.result && response.data.result.uid) {
        const sessionId = response.data.result.session_id;
        const userInfo = {
          uid: response.data.result.uid,
          username: response.data.result.username,
          name: response.data.result.name,
          email: response.data.result.email,
        };

        localStorage.setItem('odoo_session_id', sessionId);
        localStorage.setItem('user_info', JSON.stringify(userInfo));

        try {
          const permissionsResponse = await this.getUserPermissions();
          if (permissionsResponse?.status !== 'success') {
            await this.logout();
            return { success: false, error: 'Acceso no autorizado' };
          }
          return { success: true, user: userInfo };
        } catch (permissionsError) {
          await this.logout();
          return { success: false, error: 'Acceso no autorizado' };
        }
      } else {
        return { success: false, error: 'Credenciales inválidas' };
      }
    } catch (error) {
      console.error('Error en login:', error);
      return { success: false, error: 'Error de conexión con el servidor' };
    }
  }

  // Cerrar sesión
  async logout() {
    try {
      await this.api.post('/web/session/destroy');
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      localStorage.removeItem('odoo_session_id');
      localStorage.removeItem('user_info');
    }
  }

  // Verificar si el usuario está autenticado
  isAuthenticated() {
    return !!localStorage.getItem('odoo_session_id');
  }

  // Obtener información del usuario
  getUserInfo() {
    const userInfo = localStorage.getItem('user_info');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  // Obtener solicitudes de devolución (paginadas)
  async getRefundRequests({ page = 1, pageSize = 20, q = '', state = '', dateFrom = '', dateTo = '' } = {}) {
    try {
      const response = await this.api.get('/api/fad/solicitudes', {
        params: {
          page,
          page_size: pageSize,
          q: q || undefined,
          state: state || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }
      });
      return response.data || { results: [], total: 0, page, page_size: pageSize };
    } catch (error) {
      console.error('Error obteniendo solicitudes:', error);
      throw error;
    }
  }

  async getRefundRequestsAll({ q = '', state = '', dateFrom = '', dateTo = '' } = {}) {
    const all = [];
    let page = 1;
    const pageSize = 200;

    while (true) {
      const payload = await this.getRefundRequests({ page, pageSize, q, state, dateFrom, dateTo });
      const results = payload?.results || [];
      const total = payload?.total || 0;

      all.push(...results);

      if (all.length >= total) break;
      if (results.length === 0) break;
      page += 1;
    }

    return all;
  }

  // Obtener una solicitud específica
  async getRefundRequest(id) {
    try {
      const response = await this.api.get(`/api/fad/solicitudes/${id}`);
      return response.data || null;
    } catch (error) {
      console.error('Error obteniendo solicitud:', error);
      throw error;
    }
  }

  // Actualizar una solicitud
  async updateRefundRequest(id, data) {
    try {
      const response = await this.api.post(`/api/fad/solicitudes/${id}/save`, data);
      return response.data;
    } catch (error) {
      console.error('Error actualizando solicitud:', error);
      throw error;
    }
  }

  // Enviar solicitud a firma
  async sendToSignature(id) {
    try {
      const response = await this.api.post(`/api/fad/solicitudes/${id}/send_fad`, {});
      return response.data;
    } catch (error) {
      console.error('Error enviando a firma:', error);
      throw error;
    }
  }
  
  // Enviar solicitud a FAD con motivo de validación (para usuarios is_fad_readonly)
  async sendToFadWithValidation(id, motivoValidacion) {
    try {
      const response = await this.api.post(`/api/fad/solicitudes/${id}/validacion/send_fad`, {
        motivo_validacion: motivoValidacion
      });
      return response.data;
    } catch (error) {
      console.error('Error enviando a FAD con validación:', error);
      throw error;
    }
  }

  // Obtener permisos del usuario
  async getUserPermissions() {
    try {
      const response = await this.api.get('/api/fad/get/permissions');
      return response.data;
    } catch (error) {
      console.error('Error getting user permissions:', error);
      throw error;
    }
  }

  async adminGetUsers() {
    try {
      const response = await this.api.get('/api/fad/admin/users');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo usuarios del portal:', error);
      throw error;
    }
  }

  async adminCreateUser(payload) {
    try {
      const response = await this.api.post('/api/fad/admin/users/create', payload);
      return response.data;
    } catch (error) {
      console.error('Error creando usuario del portal:', error);
      throw error;
    }
  }

  async adminUpdateUser(userId, payload) {
    try {
      const response = await this.api.post(`/api/fad/admin/users/${userId}/update`, payload);
      return response.data;
    } catch (error) {
      console.error('Error actualizando usuario del portal:', error);
      throw error;
    }
  }

  async adminChangeUserPassword(userId, password) {
    try {
      const response = await this.api.post(`/api/fad/admin/users/${userId}/password`, { password });
      return response.data;
    } catch (error) {
      console.error('Error cambiando contraseña de usuario del portal:', error);
      throw error;
    }
  }

  async adminDeleteUser(userId) {
    try {
      const response = await this.api.post(`/api/fad/admin/users/${userId}/delete`, {});
      return response.data;
    } catch (error) {
      console.error('Error eliminando usuario del portal:', error);
      throw error;
    }
  }

  async cancelSolicitud(solicitudId, motivo) {
    try {
      const response = await this.api.post(`/api/fad/solicitud/${solicitudId}/cancel`, { motivo });
      return response.data;
    } catch (error) {
      console.error('Error cancelando solicitud:', error);
      throw error;
    }
  }

  async restoreSolicitudFromCancel(solicitudId) {
    try {
      const response = await this.api.post(`/api/fad/solicitud/${solicitudId}/restore_from_cancel`, {});
      return response.data;
    } catch (error) {
      console.error('Error restaurando solicitud:', error);
      throw error;
    }
  }

  async resendSignatureEmail(solicitudId) {
    try {
      const response = await this.api.post(`/api/fad/solicitud/${solicitudId}/resend_signature_email`, {});
      return response.data;
    } catch (error) {
      console.error('Error reenviando correo de firma:', error);
      throw error;
    }
  }

  // Marcar solicitud como pagada (opcionalmente con fecha programada y detalles)
  async setStatePaymented(id, params = {}) {
    try {
      const payload = {};
      if (Object.prototype.hasOwnProperty.call(params, 'fecha_prog_pago')) {
        payload.fecha_prog_pago = params.fecha_prog_pago || null;
      }
      if (Object.prototype.hasOwnProperty.call(params, 'payment_details')) {
        payload.payment_details = params.payment_details || '';
      }
      const response = await this.api.post(`/api/fad/solicitud/${id}/set_state_paymented`, payload);
      return response.data;
    } catch (error) {
      console.error('Error setting state to paymented:', error);
      throw error;
    }
  }

  // Establecer fecha programada de pago (edición individual)
  async setFechaProgPago(solicitudId, fechaProgPago) {
    try {
      const response = await this.api.post(`/api/fad/solicitud/${solicitudId}/set_fecha_prog_pago`, {
        fecha_prog_pago: fechaProgPago || null,
      });
      return response.data;
    } catch (error) {
      console.error('Error al actualizar fecha programada de pago:', error);
      throw error;
    }
  }

  // Regresar solicitud a captura
  async returnSolicitudToCapture(id) {
    try {
      const response = await this.api.post(`/api/fad/solicitud/${id}/return_to_capture`, {});
      return response.data;
    } catch (error) {
      console.error('Error returning request to capture:', error);
      throw error;
    }
  }

  // Establecer tipo de pago
  async setTipoPago(solicitudId, tipoPago) {
    try {
      const response = await this.api.post(`/api/fad/solicitud/${solicitudId}/set_tipo_pago`, {
        tipo_pago: tipoPago
      });
      return response.data;
    } catch (error) {
      console.error('Error al actualizar el tipo de pago:', error);
      throw error;
    }
  }

  async getSolicitudDocuments(solicitudId) {
    try {
      const response = await this.api.get(`/api/fad/solicitudes/${solicitudId}/documents`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo documentos de solicitud:', error);
      throw error;
    }
  }

  async getSolicitudDocumentContent(solicitudId, document, relatedId = false) {
    try {
      const params = { document };
      if (relatedId !== false && relatedId !== null && relatedId !== undefined) {
        params.related_id = relatedId;
      }

      const response = await this.api.get(`/api/fad/solicitudes/${solicitudId}/documents/content`, {
        params,
        responseType: 'blob',
      });

      return {
        blob: response.data,
        contentType: response.headers?.['content-type'] || '',
      };
    } catch (error) {
      console.error('Error obteniendo contenido de documento:', error);
      throw error;
    }
  }

  async getResopSignatureFiles(requisitionId) {
    try {
      const response = await this.api.get(`/api/resop/internal/signature/${encodeURIComponent(requisitionId)}/files`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo archivos de firma (RESOP):', error);
      throw error;
    }
  }

  async getResopSignatureRequestFileContent(requisitionId, docKey) {
    try {
      const response = await this.api.get(
        `/api/resop/internal/signature/${encodeURIComponent(requisitionId)}/request/${encodeURIComponent(docKey)}`,
        { responseType: 'blob' }
      );
      return {
        blob: response.data,
        contentType: response.headers?.['content-type'] || '',
      };
    } catch (error) {
      console.error('Error obteniendo archivo de solicitud (RESOP):', error);
      throw error;
    }
  }

  async getResopSignatureSignerFileContent(requisitionId, signerId, docKey) {
    try {
      const response = await this.api.get(
        `/api/resop/internal/signature/${encodeURIComponent(requisitionId)}/signer/${signerId}/${encodeURIComponent(docKey)}`,
        { responseType: 'blob' }
      );
      return {
        blob: response.data,
        contentType: response.headers?.['content-type'] || '',
      };
    } catch (error) {
      console.error('Error obteniendo archivo de firmante (RESOP):', error);
      throw error;
    }
  }

  async deleteSolicitudDocument(solicitudId, document, relatedId = false) {
    try {
      const payload = { document };
      if (relatedId !== false && relatedId !== null && relatedId !== undefined) {
        payload.related_id = relatedId;
      }

      const response = await this.api.post(`/api/fad/solicitudes/${solicitudId}/documents/delete`, payload);
      return response.data;
    } catch (error) {
      console.error('Error eliminando documento de solicitud:', error);
      throw error;
    }
  }

  async rejectSolicitudDocument(solicitudId, document, relatedId = false, returnToDraft = false, motivoDeRechazo = '', documentoRechazado = '', documentTypeLabel = '') {
    try {
      const payload = {
        document,
        return_to_draft: returnToDraft,
        motivo_de_rechazo: motivoDeRechazo,
        documento_rechazado: documentoRechazado,
        document_type_label: documentTypeLabel,
      };
      if (relatedId !== false && relatedId !== null && relatedId !== undefined) {
        payload.related_id = relatedId;
      }
      const response = await this.api.post(`/api/fad/solicitudes/${solicitudId}/documents/reject`, payload);
      return response.data;
    } catch (error) {
      console.error('Error rechazando documento de solicitud:', error);
      throw error;
    }
  }

  async getSolicitudChatter(solicitudId) {
    try {
      const response = await this.api.get(`/api/fad/solicitudes/${solicitudId}/chatter`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo chatter de solicitud:', error);
      throw error;
    }
  }

  async postSolicitudChatterMessage(solicitudId, body) {
    try {
      const response = await this.api.post(`/api/fad/solicitudes/${solicitudId}/chatter/message`, { body });
      return response.data;
    } catch (error) {
      console.error('Error publicando mensaje en chatter:', error);
      throw error;
    }
  }

  async createSolicitud(data) {
    try {
      const response = await this.api.post('/api/fad/solicitudes/create', data);
      return response.data;
    } catch (error) {
      console.error('Error creando solicitud:', error);
      throw error;
    }
  }

  async uploadSolicitudDocument(solicitudId, document, file, { montoEsperado } = {}) {
    try {
      const formData = new FormData();
      formData.append('document', document);
      formData.append('file', file);
      if (montoEsperado !== undefined && montoEsperado !== null && montoEsperado !== '' && !Number.isNaN(Number(montoEsperado))) {
        formData.append('monto_esperado', String(montoEsperado));
      }

      const response = await this.api.post(`/api/fad/solicitudes/${solicitudId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error cargando documento en solicitud:', error);
      throw error;
    }
  }

  async createTomaUnidad(solicitudId, payload) {
    try {
      const response = await this.api.post(`/api/fad/solicitudes/${solicitudId}/toma_unidad/create`, payload);
      return response.data;
    } catch (error) {
      console.error('Error generando toma de unidad:', error);
      throw error;
    }
  }

  // Obtener traducción de estado
  getStateTranslation(state) {
    const translations = {
      'Draft': 'En Progreso',
      'Sent': 'Enviado a Firma',
      'Pending': 'Pendiente de Firmar',
      'Signed': 'Firmado',
      'Expired': 'Expirado',
      'Paymented': 'Pagado',
      'Cancelled': 'Cancelado',
      'Rejected': 'Rechazado',
      'Rejected_User': 'Rechazado por el Firmante',
      'onValidation': 'Debe Validar'
    };
    return translations[state] || state;
  }

  getStateDescription(state) {
    const descriptions = {
      'Draft': 'El proceso de captura de información esta siendo atendida por la automatización, se enviará a FAD de forma automática cuando el proceso termine.',
      'Sent': 'Se ha enviado la información a FAD, el usuario ah sido dado de alta y se ha generado el documento de firma.',
      'Pending': 'Han transcurrido 24hrs de espera de firma del cliente en FAD.',
      'Signed': 'La firma del documento ah finalizado, debería recibir los documentos por correo y presionar: Enviar notificación de pago, cuando la orden de pago se haya generado.',
      'Paymented': 'El cliente ah recibido la notificación de su pago con respecto a su solicitud de devolución.',
      'Expired': 'Ah transcurrido más de 30 días y el cliente no realizo su firma correspondiente.',
      'Cancelled': 'Se ha cancelado esta firma desde la plataforma de FAD.',
      'Rejected': 'La firma fue rechazado desde FAD (Pueden ser diversos motivos, si es necesario revise en FAD los detalles).',
      'Rejected_User': 'La firma fue rechazada por el cliente.',
      'onValidation': 'El usuario debe validar la información completa de la solicitud antes de enviarla a Firma, es muy importante revisar todos los campos y documentos.'
    };
    return descriptions[state] || 'Estado no reconocido';
  }
}

export default new ApiService();
